/**
 * Group Financials — the Standard Return (Dipali's June-2026 note).
 * One picture of the Group, every month, from one set of numbers.
 * Tabs: Monthly Actuals · Standard Return input · Sales Pipeline · Q2 Outlook.
 */
import React, { useEffect, useState } from "react";
import { BarChart3, PenLine, TrendingUp, Target, Loader2, CheckCircle2, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/group-mis`;
const headers = () => {
  const tok = localStorage.getItem("access_token") || localStorage.getItem("token");
  return { "Content-Type": "application/json", ...(tok ? { Authorization: `Bearer ${tok}` } : {}) };
};
const NAVY = "#1B2A4A";
const fmtL = (v) => (v === null || v === undefined) ? "—" :
  `₹${(v / 100000).toLocaleString("en-IN", { maximumFractionDigits: 1 })}L`;
const fmtPct = (v) => (v === null || v === undefined) ? "—" : `${(v * 100).toFixed(1)}%`;
const MONTH_LABEL = (m) => new Date(`${m}-01`).toLocaleString("en", { month: "short", year: "2-digit" });

export default function GroupFinancialsPage() {
  const [tab, setTab] = useState("actuals");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/summary`, { headers: headers(), credentials: "include" });
      setSummary(await r.json());
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const TABS = [
    { id: "actuals", label: "Monthly Actuals", icon: BarChart3 },
    { id: "return", label: "Standard Return", icon: PenLine },
    { id: "pipeline", label: "Sales Pipeline", icon: Target },
    { id: "q2", label: "Q2 Outlook", icon: TrendingUp },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="group-financials-page">
      <div className="mb-4">
        <h1 className="text-2xl font-bold" style={{ color: NAVY }}>Group Financials — Standard Return</h1>
        <p className="text-sm text-gray-500">One picture of the Group, every month, from one set of numbers · pre-tax · net of GST</p>
      </div>
      <div className="flex gap-2 mb-5 flex-wrap">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`gf-tab-${t.id}`}
            className="px-3 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 border"
            style={tab === t.id ? { background: NAVY, color: "white", borderColor: NAVY }
                                : { color: NAVY, borderColor: "#cbd5e1" }}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>
      {loading ? <div className="py-16 text-center text-gray-400"><Loader2 className="animate-spin inline" /> Loading…</div> : (
        <>
          {tab === "actuals" && <ActualsTab summary={summary} />}
          {tab === "return" && <ReturnTab summary={summary} onSaved={load} />}
          {tab === "pipeline" && <PipelineTab entities={summary?.entities || {}} />}
          {tab === "q2" && <Q2Tab />}
        </>
      )}
    </div>
  );
}

function ActualsTab({ summary }) {
  if (!summary?.rows?.length) return <EmptyCard text="No returns yet — seed Q1 or submit via the Standard Return tab." />;
  const { entities, rows, months, group_by_month } = summary;
  const latest = months[months.length - 1];
  const g = group_by_month[latest] || {};
  const cell = (e, m, f) => {
    const r = rows.find(x => x.entity === e && x.month === m);
    return r ? r[f] : null;
  };
  const qTotal = (e, f) => rows.filter(x => x.entity === e).reduce((s, r) => s + (r[f] || 0), 0);
  return (
    <div data-testid="gf-actuals">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label={`Group Revenue (${MONTH_LABEL(latest)})`} value={fmtL(g.revenue)} />
        <StatCard label={`Group Net pre-tax (${MONTH_LABEL(latest)})`} value={fmtL(g.net_pretax)} accent={g.net_pretax >= 0 ? "#0d7a3f" : "#b91c1c"} />
        <StatCard label="Group Net Margin" value={fmtPct(g.net_margin)} />
        <StatCard label="Units Reported" value={`${g.units_reported || 0} / 5`} />
      </div>
      {["revenue", "net_pretax"].map(f => (
        <div key={f} className="mb-6 bg-white rounded-xl border shadow-sm overflow-x-auto">
          <div className="px-4 pt-3 font-semibold text-sm" style={{ color: NAVY }}>
            {f === "revenue" ? "REVENUE BY REPORTING UNIT" : "NET RESULT (PRE-TAX) BY UNIT"}
          </div>
          <table className="w-full text-sm mt-2" data-testid={`gf-table-${f}`}>
            <thead><tr className="text-left text-xs text-gray-500 border-b">
              <th className="px-4 py-2">Unit</th>
              {months.map(m => <th key={m} className="px-3 py-2 text-right">{MONTH_LABEL(m)}</th>)}
              <th className="px-4 py-2 text-right">Total</th>
            </tr></thead>
            <tbody>
              {Object.entries(entities).map(([eid, e]) => (
                <tr key={eid} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium">{e.name}</td>
                  {months.map(m => {
                    const v = cell(eid, m, f);
                    return <td key={m} className={`px-3 py-2 text-right ${f === "net_pretax" && v < 0 ? "text-red-600" : ""}`}>{fmtL(v)}</td>;
                  })}
                  <td className={`px-4 py-2 text-right font-semibold ${f === "net_pretax" && qTotal(eid, f) < 0 ? "text-red-600" : ""}`}>{fmtL(qTotal(eid, f))}</td>
                </tr>
              ))}
              <tr className="font-bold" style={{ color: NAVY }}>
                <td className="px-4 py-2">GROUP</td>
                {months.map(m => <td key={m} className="px-3 py-2 text-right">{fmtL(group_by_month[m]?.[f])}</td>)}
                <td className="px-4 py-2 text-right">{fmtL(months.reduce((s, m) => s + (group_by_month[m]?.[f] || 0), 0))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function ReturnTab({ summary, onSaved }) {
  const entities = summary?.entities || {};
  const months = summary?.months || [];
  const now = new Date();
  const defMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(months[months.length - 1] || defMonth);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState(null);
  const rowFor = (e) => summary?.rows?.find(r => r.entity === e && r.month === month) || {};
  const val = (e, f) => drafts[`${e}.${f}`] ?? (rowFor(e)[f] ?? "");
  const setVal = (e, f, v) => setDrafts(d => ({ ...d, [`${e}.${f}`]: v }));

  const save = async (e) => {
    setSaving(e); setMsg(null);
    const body = { entity: e, month };
    ["revenue", "direct_cost", "opex", "net_pretax", "closing_cash", "notes"].forEach(f => {
      const v = val(e, f);
      if (v !== "" && v !== null && v !== undefined) body[f] = f === "notes" ? v : Number(v);
    });
    const r = await fetch(`${API}/returns`, { method: "POST", headers: headers(), credentials: "include", body: JSON.stringify(body) });
    const j = await r.json();
    setSaving(null);
    if (!r.ok) { setMsg(j.detail || "Save failed"); return; }
    setMsg(`${entities[e]?.name} saved`); setDrafts({}); onSaved();
  };
  const sign = async (e) => {
    const r = await fetch(`${API}/returns/sign`, { method: "POST", headers: headers(), credentials: "include", body: JSON.stringify({ entity: e, month }) });
    if (r.ok) { setMsg(`${entities[e]?.name} signed off`); onSaved(); }
  };

  return (
    <div data-testid="gf-return">
      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm text-gray-600">Month</label>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          data-testid="gf-return-month" className="border rounded px-2 py-1 text-sm" />
        {msg && <span className="text-sm text-emerald-700" data-testid="gf-return-msg">{msg}</span>}
      </div>
      <div className="bg-white rounded-xl border shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-gray-500 border-b">
            <th className="px-4 py-2">Unit</th>
            <th className="px-2 py-2 text-right">Revenue</th>
            <th className="px-2 py-2 text-right">Direct cost</th>
            <th className="px-2 py-2 text-right">Operating exp</th>
            <th className="px-2 py-2 text-right">Net (pre-tax)</th>
            <th className="px-2 py-2 text-right">Closing cash</th>
            <th className="px-2 py-2">Notes</th>
            <th className="px-2 py-2">Status</th>
            <th className="px-2 py-2"></th>
          </tr></thead>
          <tbody>
            {Object.entries(entities).map(([eid, e]) => {
              const row = rowFor(eid);
              return (
                <tr key={eid} className="border-b last:border-0 align-top">
                  <td className="px-4 py-2 font-medium whitespace-nowrap">{e.name}<div className="text-[10px] text-gray-400">{e.owner}</div></td>
                  {["revenue", "direct_cost", "opex", "net_pretax", "closing_cash"].map(f => (
                    <td key={f} className="px-2 py-2">
                      <input type="number" value={val(eid, f)} onChange={ev => setVal(eid, f, ev.target.value)}
                        data-testid={`gf-input-${eid}-${f}`}
                        className="w-28 border rounded px-2 py-1 text-right text-xs" />
                    </td>
                  ))}
                  <td className="px-2 py-2">
                    <input value={val(eid, "notes")} onChange={ev => setVal(eid, "notes", ev.target.value)}
                      data-testid={`gf-input-${eid}-notes`} placeholder="one-offs, flags…"
                      className="w-36 border rounded px-2 py-1 text-xs" />
                  </td>
                  <td className="px-2 py-2 text-xs">
                    {row.status === "signed"
                      ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={12} /> signed</span>
                      : (row.status || "—")}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    <button onClick={() => save(eid)} disabled={saving === eid}
                      data-testid={`gf-save-${eid}`}
                      className="text-xs px-2 py-1 rounded text-white mr-1" style={{ background: NAVY }}>
                      {saving === eid ? "…" : "Save"}
                    </button>
                    <button onClick={() => sign(eid)} data-testid={`gf-sign-${eid}`}
                      className="text-xs px-2 py-1 rounded border" style={{ color: NAVY }}>Sign</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400 mt-2">Five lines, tied to source, pre-tax and pre-exceptional. If it doesn't reconcile to your own P&L, fix it before you submit — no placeholders, no silent gaps.</p>
    </div>
  );
}

function PipelineTab({ entities }) {
  const [items, setItems] = useState([]);
  const [stages, setStages] = useState([]);
  const [form, setForm] = useState({ entity: "cc", stage: "Lead", probability: 50 });
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const r = await fetch(`${API}/pipeline`, { headers: headers(), credentials: "include" });
    const j = await r.json();
    setItems(j.items || []); setStages(j.stages || []);
  };
  useEffect(() => { load(); }, []);
  const add = async () => {
    setBusy(true);
    const now = new Date();
    const body = { month: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`, ...form };
    const r = await fetch(`${API}/pipeline`, { method: "POST", headers: headers(), credentials: "include", body: JSON.stringify(body) });
    setBusy(false);
    if (r.ok) { setForm({ entity: form.entity, stage: "Lead", probability: 50 }); load(); }
  };
  const del = async (id) => {
    await fetch(`${API}/pipeline/${id}`, { method: "DELETE", headers: headers(), credentials: "include" });
    load();
  };
  const totalWeighted = items.reduce((s, i) => s + (i.weighted_value || 0), 0);
  return (
    <div data-testid="gf-pipeline">
      <div className="bg-white rounded-xl border shadow-sm p-4 mb-4">
        <div className="font-semibold text-sm mb-2" style={{ color: NAVY }}>Add opportunity — a number is required even when a deal is early</div>
        <div className="flex flex-wrap gap-2 items-end">
          <select value={form.entity} onChange={e => setForm({ ...form, entity: e.target.value })}
            data-testid="gf-pl-entity" className="border rounded px-2 py-1 text-sm">
            {Object.entries(entities).map(([id, e]) => <option key={id} value={id}>{e.name}</option>)}
          </select>
          <input placeholder="Opportunity" value={form.opportunity || ""} onChange={e => setForm({ ...form, opportunity: e.target.value })}
            data-testid="gf-pl-opportunity" className="border rounded px-2 py-1 text-sm w-44" />
          <input placeholder="Client" value={form.client || ""} onChange={e => setForm({ ...form, client: e.target.value })}
            data-testid="gf-pl-client" className="border rounded px-2 py-1 text-sm w-36" />
          <select value={form.stage} onChange={e => setForm({ ...form, stage: e.target.value })}
            data-testid="gf-pl-stage" className="border rounded px-2 py-1 text-sm">
            {stages.map(s => <option key={s}>{s}</option>)}
          </select>
          <input type="number" placeholder="Value ₹" value={form.value || ""} onChange={e => setForm({ ...form, value: e.target.value })}
            data-testid="gf-pl-value" className="border rounded px-2 py-1 text-sm w-28" />
          <input type="number" placeholder="Prob %" value={form.probability} onChange={e => setForm({ ...form, probability: e.target.value })}
            data-testid="gf-pl-prob" className="border rounded px-2 py-1 text-sm w-20" />
          <input type="month" value={form.expected_month || ""} onChange={e => setForm({ ...form, expected_month: e.target.value })}
            data-testid="gf-pl-expected" className="border rounded px-2 py-1 text-sm" />
          <button onClick={add} disabled={busy || !form.opportunity}
            data-testid="gf-pl-add" className="text-sm px-3 py-1.5 rounded text-white" style={{ background: NAVY }}>Add</button>
        </div>
      </div>
      <div className="bg-white rounded-xl border shadow-sm overflow-x-auto">
        <div className="px-4 pt-3 pb-1 text-sm font-semibold flex justify-between" style={{ color: NAVY }}>
          <span>Named forward opportunities</span>
          <span data-testid="gf-pl-weighted-total">Weighted: {fmtL(totalWeighted)}</span>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-gray-500 border-b">
            <th className="px-4 py-2">Unit</th><th className="px-2 py-2">Opportunity</th><th className="px-2 py-2">Client</th>
            <th className="px-2 py-2">Stage</th><th className="px-2 py-2 text-right">Value</th>
            <th className="px-2 py-2 text-right">Prob</th><th className="px-2 py-2 text-right">Weighted</th>
            <th className="px-2 py-2">Expected</th><th></th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-400">No pipeline rows yet — every unit, every month.</td></tr>}
            {items.map(i => (
              <tr key={i.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`gf-pl-row-${i.id}`}>
                <td className="px-4 py-2">{entities[i.entity]?.name || i.entity}</td>
                <td className="px-2 py-2">{i.opportunity}</td>
                <td className="px-2 py-2">{i.client}</td>
                <td className="px-2 py-2"><span className="text-xs px-2 py-0.5 rounded-full border">{i.stage}</span></td>
                <td className="px-2 py-2 text-right">{fmtL(i.value)}</td>
                <td className="px-2 py-2 text-right">{i.probability}%</td>
                <td className="px-2 py-2 text-right font-medium">{fmtL(i.weighted_value)}</td>
                <td className="px-2 py-2">{i.expected_month || "—"}</td>
                <td className="px-2 py-2">
                  <button onClick={() => del(i.id)} data-testid={`gf-pl-del-${i.id}`} className="text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Q2Tab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API}/q2-outlook`, { headers: headers(), credentials: "include" })
      .then(r => r.json()).then(setData);
  }, []);
  if (!data) return <EmptyCard text="Loading…" />;
  const { entities, outlook, secured_wins, totals } = data;
  const L = (v) => (v === null || v === undefined) ? "—" : `₹${v}L`;
  return (
    <div data-testid="gf-q2">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Q2 Base Plan (net)" value={L(totals.q2_base)} />
        <StatCard label="+ Secured Wins" value={L(totals.secured_wins)} accent="#0d7a3f" />
        <StatCard label="Q2 Outlook (net)" value={L(totals.q2_outlook)} />
        <StatCard label="Secured Deal Value" value={L(totals.wins_deal_value)} />
      </div>
      <div className="bg-white rounded-xl border shadow-sm overflow-x-auto mb-5">
        <div className="px-4 pt-3 font-semibold text-sm" style={{ color: NAVY }}>Q2 (JUL–SEP) FORWARD OUTLOOK — net result, ₹ lakhs</div>
        <table className="w-full text-sm mt-2" data-testid="gf-q2-table">
          <thead><tr className="text-left text-xs text-gray-500 border-b">
            <th className="px-4 py-2">Business (Owner)</th><th className="px-2 py-2 text-right">Q1 actual</th>
            <th className="px-2 py-2 text-right">Q2 base</th><th className="px-2 py-2 text-right">+ Wins</th>
            <th className="px-2 py-2 text-right">Q2 outlook</th>
            <th className="px-2 py-2 text-right">Jul</th><th className="px-2 py-2 text-right">Aug</th><th className="px-2 py-2 text-right">Sep</th>
          </tr></thead>
          <tbody>
            {outlook.map(o => (
              <tr key={o.entity} className="border-b last:border-0">
                <td className="px-4 py-2 font-medium">{entities[o.entity]?.name} <span className="text-[10px] text-gray-400">({entities[o.entity]?.owner})</span></td>
                {["q1_actual", "q2_base", "secured_wins", "q2_outlook", "jul", "aug", "sep"].map(f => (
                  <td key={f} className={`px-2 py-2 text-right ${o[f] < 0 ? "text-red-600" : ""}`}>{o[f] === 0 && f === "secured_wins" ? "—" : L(o[f])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="bg-white rounded-xl border shadow-sm overflow-x-auto">
        <div className="px-4 pt-3 font-semibold text-sm" style={{ color: NAVY }}>SECURED WINS FEEDING Q2</div>
        <table className="w-full text-sm mt-2" data-testid="gf-q2-wins">
          <thead><tr className="text-left text-xs text-gray-500 border-b">
            <th className="px-4 py-2">Win</th><th className="px-2 py-2">Owner</th><th className="px-2 py-2 text-right">Deal ₹L</th>
            <th className="px-2 py-2">Split to</th><th className="px-2 py-2">Recognition</th>
            <th className="px-2 py-2 text-right">Monthly ₹L</th><th className="px-2 py-2">Cash status</th>
          </tr></thead>
          <tbody>
            {secured_wins.map((w, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="px-4 py-2">{w.win}</td><td className="px-2 py-2">{w.owner}</td>
                <td className="px-2 py-2 text-right">{w.deal_value_lakh}</td><td className="px-2 py-2">{w.split_to}</td>
                <td className="px-2 py-2">{w.recognition}</td><td className="px-2 py-2 text-right">{w.monthly_lakh}</td>
                <td className="px-2 py-2 text-xs">{w.cash_status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const StatCard = ({ label, value, accent }) => (
  <div className="bg-white rounded-xl border shadow-sm p-4">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-xl font-bold mt-1" style={{ color: accent || NAVY }}>{value}</div>
  </div>
);
const EmptyCard = ({ text }) => (
  <div className="bg-white rounded-xl border shadow-sm p-10 text-center text-gray-400">{text}</div>
);
